import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import {
  getBalance,
  listWalletTransactions,
  type WalletTransaction,
} from '@/lib/wallet';
import { convertAmount, normalizeCurrency } from '@/lib/currency';
import { supabase } from '@/lib/supabase';
import { listCommissionsForParrain, type CommissionParrainage } from '@/lib/parrainage';
import WalletTopUpModal from '@/components/feature/WalletTopUpModal';
import WithdrawalPanel from '@/components/feature/WithdrawalPanel';
import EarningsTab from '@/pages/dashboard/wallet/components/EarningsTab';

interface MissionDue {
  id: number;
  titre: string;
  monaie: string;
  creators: number;
  total: number;
}

const METHOD_META: Record<string, { label: string; icon: string; className: string }> = {
  wallet: { label: 'Portefeuille', icon: 'ri-wallet-3-line', className: 'bg-primary-100 text-primary-700' },
  admin: { label: 'Administrateur', icon: 'ri-shield-star-line', className: 'bg-secondary-100 text-secondary-700' },
  paypal: { label: 'PayPal', icon: 'ri-paypal-line', className: 'bg-accent-100 text-accent-700' },
  stripe: { label: 'Stripe', icon: 'ri-bank-card-line', className: 'bg-accent-100 text-accent-700' },
  feexpay: { label: 'Mobile Money', icon: 'ri-smartphone-line', className: 'bg-accent-100 text-accent-700' },
  vente_boutique: { label: 'Vente boutique', icon: 'ri-store-3-line', className: 'bg-accent-100 text-accent-700' },
  retrait: { label: 'Retrait', icon: 'ri-bank-card-2-line', className: 'bg-secondary-100 text-secondary-700' },
  parrainage: { label: 'Parrainage', icon: 'ri-share-circle-line', className: 'bg-accent-100 text-accent-700' },
};

function methodMeta(m: string) {
  return METHOD_META[m] || { label: m, icon: 'ri-bank-card-line', className: 'bg-background-100 text-foreground-600' };
}

function formatMontant(n: number, monaie: string): string {
  return `${Number(n).toLocaleString('fr-FR')} ${monaie}`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function DashboardWalletPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [balance, setBalance] = useState<number>(user?.solde || 0);
  const [txs, setTxs] = useState<WalletTransaction[]>([]);
  const [commissions, setCommissions] = useState<CommissionParrainage[]>([]);
  const [missionsDue, setMissionsDue] = useState<MissionDue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [tab, setTab] = useState<'overview' | 'earnings'>('overview');

  const monaie = user?.monaie || 'MAD';

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const [b, t, c] = await Promise.all([
        getBalance(user.id),
        listWalletTransactions(user.id, 100),
        listCommissionsForParrain(user.id),
      ]);
      setBalance(b);
      setTxs(t);
      setCommissions(c);
      // Récapitulatif des montants à régler aux créateurs (missions validées).
      try {
        const { data: missionsRows } = await supabase
          .from('missions')
          .select('id, titre, monaie')
          .eq('user_id', user.id);
        const rows = (missionsRows || []) as { id: number; titre: string; monaie: string | null }[];
        const ids = rows.map((m) => Number(m.id)).filter(Boolean);
        if (ids.length > 0) {
          const { data: cands } = await supabase
            .from('mission_candidatures')
            .select('mission_id, montant_du, statut')
            .in('mission_id', ids)
            .eq('statut', 'validee');
          const grouped = new Map<number, { total: number; creators: number }>();
          ((cands || []) as { mission_id: number; montant_du: number | null }[]).forEach((c) => {
            const key = Number(c.mission_id);
            const current = grouped.get(key) || { total: 0, creators: 0 };
            current.total += Number(c.montant_du) || 0;
            current.creators += 1;
            grouped.set(key, current);
          });
          setMissionsDue(
            rows
              .filter((m) => grouped.has(Number(m.id)))
              .map((m) => {
                const g = grouped.get(Number(m.id))!;
                return {
                  id: Number(m.id),
                  titre: m.titre,
                  monaie: normalizeCurrency(m.monaie || monaie),
                  creators: g.creators,
                  total: Math.round(g.total * 100) / 100,
                };
              })
              .sort((a, b) => b.total - a.total),
          );
        } else {
          setMissionsDue([]);
        }
      } catch {
        setMissionsDue([]);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">{t('wallet.title', { defaultValue: 'Portefeuille' })}</h2>
        <p className="text-sm text-foreground-500 mt-1">
          {t('wallet.subtitle', { defaultValue: 'Votre solde, vos recharges et l’historique de vos opérations.' })}
        </p>
      </div>

      {/* Onglets */}
      <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-1 w-fit mb-5">
        {([
          { key: 'overview' as const, label: 'Portefeuille', icon: 'ri-wallet-3-line' },
          { key: 'earnings' as const, label: 'Mes gains', icon: 'ri-line-chart-line' },
        ]).map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
              tab === item.key ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className={`${item.icon} text-sm`}></i>
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'earnings' ? (
        <EarningsTab
          userId={user?.id || 0}
          currency={monaie}
          onRequestWithdrawal={() => { setTab('overview'); }}
        />
      ) : (
        <>
      {/* Solde + recharge */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <div className="flex items-center gap-2 text-foreground-500 text-sm mb-2">
            <i className="ri-wallet-3-line text-accent-500"></i>
            {t('wallet.available', { defaultValue: 'Solde disponible' })}
          </div>
          <div className="text-3xl font-bold font-heading text-foreground-950">
            {balance.toLocaleString('fr-FR')}{' '}
            <span className="text-base font-medium text-foreground-500">{monaie}</span>
          </div>
          <p className="text-xs text-foreground-400 mt-3">
            Ce solde encaisse vos ventes en ligne et vous permet de payer vos abonnements et services.
            Un retrait peut être demandé à tout moment.
          </p>
        </div>

        <div className="lg:col-span-2 bg-background-50 border border-background-200/70 rounded-lg p-5">
          <div className="flex items-center gap-2 text-foreground-500 text-sm mb-3">
            <i className="ri-add-circle-line text-primary-500"></i>
            {t('wallet.recharge', { defaultValue: 'Recharger le portefeuille' })}
          </div>
          <button
            type="button"
            onClick={() => setTopUpOpen(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-smartphone-line text-lg"></i>
            Recharger via Mobile Money
          </button>
          <p className="text-xs text-foreground-400 mt-3 leading-relaxed">
            Paiement sécurisé Mobile Money (FeexPay) : le montant est prélevé sur votre compte
            et votre solde Zifek est crédité automatiquement dès la confirmation de l’opérateur.
          </p>
        </div>
      </div>

      {/* Retraits */}
      <div className="mb-6">
        <WithdrawalPanel
          userId={user?.id || 0}
          currency={monaie}
          balance={balance}
          onChanged={() => { void load(); }}
        />
      </div>

      <WalletTopUpModal
        open={topUpOpen}
        userId={user?.id || 0}
        currency={monaie}
        onClose={() => setTopUpOpen(false)}
        onCredited={() => { void load(); }}
      />

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {/* Montants dus aux créateurs pour les missions validées */}
      <div className="mb-6 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-b border-background-200/70">
          <i className="ri-megaphone-line text-primary-500"></i>
          <h3 className="text-sm font-semibold text-foreground-900">Missions — montants dus</h3>
          {missionsDue.length > 0 && (
            <span className="ml-auto text-xs font-semibold text-primary-700 whitespace-nowrap">
              Total à régler :{' '}
              {missionsDue
                .reduce((sum, m) => {
                  const inMad = convertAmount(m.total, m.monaie, 'MAD');
                  return sum + (inMad ?? m.total);
                }, 0)
                .toLocaleString('fr-FR', { maximumFractionDigits: 2 })}{' '}
              MAD
            </span>
          )}
        </div>

        {missionsDue.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
            <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-3">
              <i className="ri-megaphone-line text-xl text-foreground-400"></i>
            </div>
            <p className="text-sm text-foreground-500">Aucun montant dû pour le moment.</p>
            <p className="text-xs text-foreground-400 mt-1 max-w-sm">
              Les montants apparaissent ici dès que vous validez les publications d&apos;une mission.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-background-200/60">
            {missionsDue.map((m) => (
              <li key={m.id} className="flex flex-wrap items-center gap-2 px-5 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground-900 truncate">{m.titre}</p>
                  <p className="text-[11px] text-foreground-500">
                    {m.creators} créateur{m.creators > 1 ? 's' : ''} validé{m.creators > 1 ? 's' : ''}
                  </p>
                </div>
                <span className="text-sm font-bold text-primary-700 whitespace-nowrap">
                  {m.total.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {m.monaie}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Historique */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-background-200/70">
          <i className="ri-history-line text-accent-500"></i>
          <h3 className="text-sm font-semibold text-foreground-900">{t('wallet.history', { defaultValue: 'Historique des opérations' })}</h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : txs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
              <i className="ri-wallet-3-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-sm text-foreground-500">{t('wallet.empty', { defaultValue: 'Aucune opération pour le moment.' })}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-foreground-400 border-b border-background-200/70">
                  <th className="py-2 px-5 font-medium">{t('wallet.col_type', { defaultValue: 'Type' })}</th>
                  <th className="py-2 px-3 font-medium">{t('wallet.col_amount', { defaultValue: 'Montant' })}</th>
                  <th className="py-2 px-3 font-medium">{t('wallet.col_method', { defaultValue: 'Méthode' })}</th>
                  <th className="py-2 px-3 font-medium">{t('wallet.col_details', { defaultValue: 'Détails' })}</th>
                  <th className="py-2 px-5 font-medium">{t('wallet.col_date', { defaultValue: 'Date' })}</th>
                </tr>
              </thead>
              <tbody>
                {txs.map((tx) => {
                  const meta = methodMeta(tx.methode);
                  const isCredit = tx.type === 'recharge';
                  return (
                    <tr key={tx.id} className="border-b border-background-200/50 last:border-0">
                      <td className="py-2.5 px-5">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                            isCredit ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'
                          }`}
                        >
                          <i className={`${isCredit ? 'ri-arrow-down-line' : 'ri-arrow-up-line'} text-[10px]`}></i>
                          {isCredit ? t('wallet.type_recharge', { defaultValue: 'Recharge' }) : tx.type === 'refund' ? t('wallet.type_refund', { defaultValue: 'Remboursement' }) : t('wallet.type_debit', { defaultValue: 'Débit' })}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        <span className={`text-sm font-bold ${isCredit ? 'text-accent-600' : 'text-primary-600'}`}>
                          {isCredit ? '+' : '-'}{Number(tx.montant).toLocaleString('fr-FR')} {monaie}
                        </span>
                      </td>
                      <td className="py-2.5 px-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${meta.className}`}>
                          <i className={`${meta.icon} text-[10px]`}></i>
                          {meta.label}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-foreground-600 max-w-[240px] truncate" title={tx.note || ''}>
                        {tx.note || '—'}
                      </td>
                      <td className="py-2.5 px-5 text-foreground-500 whitespace-nowrap">{formatDate(tx.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Commissions de parrainage (l'utilisateur est parrain) */}
      <div className="mt-6 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-background-200/70">
          <i className="ri-share-circle-line text-accent-500"></i>
          <h3 className="text-sm font-semibold text-foreground-900">{t('wallet.referral_commissions', { defaultValue: 'Commissions de parrainage' })}</h3>
          {commissions.length > 0 && (
            <span className="ml-auto text-xs font-semibold text-accent-700 whitespace-nowrap">
              {t('wallet.total', { defaultValue: 'Total' })} : {commissions.reduce((s, c) => s + (Number(c.commission) || 0), 0).toLocaleString('fr-FR')} {monaie}
            </span>
          )}
        </div>
        {commissions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
              <i className="ri-share-circle-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-sm text-foreground-500">{t('wallet.referral_empty', { defaultValue: 'Aucune commission de parrainage pour le moment.' })}</p>
            <p className="text-xs text-foreground-400 mt-1">Partagez votre code de parrainage pour gagner des commissions sur les abonnements.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-foreground-400 border-b border-background-200/70">
                  <th className="py-2 px-5 font-medium">{t('wallet.col_date', { defaultValue: 'Date' })}</th>
                  <th className="py-2 px-3 font-medium">{t('wallet.col_referral', { defaultValue: 'Filleul' })}</th>
                  <th className="py-2 px-3 font-medium">{t('wallet.col_code', { defaultValue: 'Code' })}</th>
                  <th className="py-2 px-3 font-medium">{t('wallet.col_plan', { defaultValue: 'Abonnement' })}</th>
                  <th className="py-2 px-5 font-medium">{t('wallet.col_commission', { defaultValue: 'Commission' })}</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((c) => (
                  <tr key={c.id} className="border-b border-background-200/50 last:border-0">
                    <td className="py-2.5 px-5 text-foreground-500 whitespace-nowrap">{formatDate(c.createdAt || '')}</td>
                    <td className="py-2.5 px-3 text-foreground-800">{c.filleulName || (c.filleulId ? `#${c.filleulId}` : '—')}</td>
                    <td className="py-2.5 px-3"><code className="text-xs text-foreground-600">{c.code}</code></td>
                    <td className="py-2.5 px-3 text-foreground-600">{c.plan || '—'}</td>
                    <td className="py-2.5 px-5 text-sm font-bold text-accent-700 whitespace-nowrap">+{Number(c.commission).toLocaleString('fr-FR')} {monaie}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
}